import React, { useState, useEffect } from "react";
import { Modal, ModalTitle, Button, Row, Col, Form, Spinner } from "react-bootstrap";
import { FaBox, FaTasks, FaArrowLeft } from "react-icons/fa";
import JajotApi from "../../api/JajotApi";
import { useLocation, useNavigate } from "react-router-dom";
import { orderHeaders, voucherHeader, ledgerHeader, stockItemHeader, taskHeaders } from "../../constants/CONSTANT";

const TYPE_MAP = {
  ledgers: { name: "Ledger", header: ledgerHeader },
  orders: { name: "Order", header: orderHeaders },
  vouchers: { name: "Voucher", header: voucherHeader },
  stockitems: { name: "Stock Item", header: stockItemHeader },
  tasks: { name: "Task", header: taskHeaders },
};

const HEADER_MAP = {
  ledgers: ledgerHeader,
  orders: orderHeaders,
  vouchers: voucherHeader,
  stockitems: stockItemHeader,
  tasks: taskHeaders,
};

const getValue = (obj, path) => {
  if (!obj || typeof obj !== "object") return undefined;
  let current = obj;

  for (const key of path.split(".")) {
    if (current == null || typeof current !== "object") return undefined;
    if (key in current) {
      current = current[key];
      continue;
    }
    if (key === "Stock_Item__r" && current.Ledger_Item__r?.Name)
      return current.Ledger_Item__r.Name;
    return undefined;
  }
  return current;
};

const formatCurrency = (val, curr = "INR") =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: curr,
    minimumFractionDigits: 2,
  }).format(val || 0);

const formatDate = (val) => {
  const d = new Date(val);
  return isNaN(d) ? val : d.toLocaleDateString();
};

const renderValue = (col, record, raw, onOpen) => {
  const val = raw ?? getValue(record, col.key);

  if (col.type === "currency") return formatCurrency(val, col.currency);
  if (col.type === "date") return formatDate(val);

  if (col.type === "link" && col.linkTo && col.key !== "Name") {
    const id = getValue(record, col.id || "Id");
    return (
      <button
        className="btn btn-link text-primary p-0"
        style={{ textDecoration: "underline", border: "none", cursor: "pointer" }}
        onClick={() => onOpen?.(id, col.linkTo)}
      >
        {val}
      </button>
    );
  }

  if (col.type === "custom" && typeof col.renderer === "function")
    return col.renderer(val, record);
  if (typeof col.renderer === "function")
    return col.renderer(record);

  if (val == null) return "—";
  if (typeof val === "object") return Object.values(val).join(", ");
  return val;
};

const buildSections = (header, record, onOpen) => {
  const sections = {};

  header.forEach((col) => {
    const section = col.section || "General";

    if (col.key.endsWith("__r") && col.values?.length) {
      const kids = record[col.key]?.records || [];
      sections[col.label] = kids.map((child) => {
        const row = {};
        col.values.forEach((f) => {
          if (f.show) row[f.label] = renderValue(f, child, child[f.key], onOpen);
        });
        return row;
      });
    }
    else if (col.inModel) {
      if (!sections[section]) sections[section] = [];
      sections[section].push({
        key: col.key,
        label: col.label,
        value: renderValue(col, record, undefined, onOpen),
      });
    }
  });

  return sections;
};

const Empty = ({ name }) => (
  <div className="text-center text-muted py-4">
    <FaBox size={32} className="mb-2 opacity-25" />
    <p className="mb-0">No {name} found</p>
  </div>
);

const Table = ({ data }) => (
  <table className="table table-sm table-hover">
    <thead>
      <tr>{Object.keys(data[0]).map((k) => <th key={k}>{k}</th>)}</tr>
    </thead>
    <tbody>
      {data.map((row, i) => (
        <tr key={i}>
          {Object.values(row).map((v, j) => (
            <td key={j}>{v}</td>
          ))}
        </tr>
      ))}
    </tbody>
  </table>
);

const FormFields = ({ fields }) => (
  <Row className="g-3 bg-white p-2">
    {fields.map((f) => (
      <Col md={6} key={f.key}>
        <Form.Group>
          <Form.Label>{f.label}</Form.Label>
          <div className="form-control bg-white">{f.value}</div>
        </Form.Group>
      </Col>
    ))}
  </Row>
);

const Section = ({ name, data }) => {
  if (data.length === 0) return <Empty name={name} />;
  if (data.length > 0 && !data[0].value) return <Table data={data} />;
  return <FormFields fields={data} />;
};

function RecordModal({ show, onHide, record, header, defaultType }) {
  const [tasks, setTasks] = useState([]);
  const [data, setData] = useState(null);
  const [cols, setCols] = useState(null);
  const [type, setType] = useState(null);
  const [stack, setStack] = useState([]);
  const [loading, setLoading] = useState(false);
  const loc = useLocation();
  const navigate = useNavigate();
  
  useEffect(() => {
    if (show && record?.Id) {
      setData(record);
      setCols(header);
      setType(defaultType || null);
      setStack([]);
      // Load related tasks only for non-task records to avoid duplicate fetches
      if (defaultType !== "tasks") {
        loadTasks(record.Id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, record?.Id, defaultType]);

  const loadTasks = async (id) => {
    // Skip fetching related tasks when already viewing a task record
    if (!id || defaultType === "tasks") return;
    try {
      const res = await JajotApi.getTasksRecords(id, null);
      const list = res?.success ? res.records || [] : [];
      setTasks(
        list.map(t => ({
          Title: t.Title__c || t.Name || "—",
          Status: t.Status__c || "New",
          Priority: t.Priority__c || "—",
          "Start Date": t.Start_Date__c ? formatDate(t.Start_Date__c) : "—",
          "End Date": t.End_Date__c ? formatDate(t.End_Date__c) : "—",
          Owner: t.OwnerId__r?.Name || t.OwnerId__r?.Full_Name__c || t.OwnerId || "—",
          Assignees: t.Task_Assignees__r?.records?.map(a => a.ContactId__r?.Name || a.ContactId__c).join(", ") || "—",
        }))
      );
    } catch (err) {
      console.error("Error loading tasks:", err);
      setTasks([]);
    }
  };

  const getHeader = (typeStr) => HEADER_MAP[typeStr] || cols;
  const getTypeName = (typeStr) => TYPE_MAP[typeStr]?.name || "Record";

  const open = async (id, typeStr) => {
    try {
      setLoading(true);
      const res = await JajotApi.getRecordById(id, typeStr);
      if (res?.success) {
        const newData = res.records[0];
        const newCols = getHeader(typeStr);

        setStack([...stack, { data, cols, type }]);
        setData(newData);
        setCols(newCols);
        setType(typeStr);
        await loadTasks(newData.Id);
      }
    } catch (err) {
      console.error("Error opening record:", err);
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    if (stack.length > 0) {
      const prev = stack[stack.length - 1];
      setData(prev.data);
      setCols(prev.cols);
      setType(prev.type);
      setStack(stack.slice(0, -1));
      loadTasks(prev.data.Id);
    }
  };

  const handleEdit = () => {
    const currentType = type || defaultType;
    if (!currentType || !data) return;

    if (currentType === "tasks") {
      const mappedTask = {
        Id: data?.Id || null,
        Name: data?.Name,
        Title: data?.Title__c || null,
        Status: data?.Status__c || "New",
        OwnerId: data?.OwnerId__c || null,
        StartDate: data?.Start_Date__c,
        EndDate: data?.End_Date__c,
        Priority: data?.Priority__c || "",
        Description: data?.Description__c || null,
        Assignees: data?.Task_Assignees__r?.records?.map((element) => ({
          recordId: element?.Id,
          assigneeId: element?.ContactId__c,
        })) || [],
      };

      navigate(`/task/edit`, { state: { existingTask: mappedTask } });
      onHide?.();
      return;
    }

    if (currentType === "orders") {
      const mappedOrder = {
        details: {
          id: data?.Id || null,
          type: data?.Type__c?.toLowerCase() || "sales",
          status: data?.Order_Status__c,
          partyLedger: data?.Party_Ledger__r && {
            value: data.Party_Ledger__r.Id,
            label: data.Party_Ledger__r.Name,
          },
          salesLedger: data?.Sales_Ledger__r && {
            value: data.Sales_Ledger__r.Id,
            label: data.Sales_Ledger__r.Name,
          },
        },
        items:
          data?.Order_Line_Items__r?.records?.map((r) => ({
            id: r.Id,
            isLedger: !!r.Ledger__r,
            item: r.Ledger__r
              ? { value: r.Ledger__r.Id, label: r.Ledger__r.Name }
              : { value: r.Stock_Item__r?.Id, label: r.Stock_Item__r?.Name },
            rate: r.Rate__c,
            quantity: r.Quantity__c,
            units: r.Stock_Item__r?.Units__c || "pcs",
            amount: r.Amount__c,
          })) || [],
      };

      navigate(`/order/edit`, { state: { existingOrder: mappedOrder } });
      onHide?.();
    }
  };

  if (!data) return null;

  const sections = buildSections(cols, data, open);

  return (
    <Modal size="lg" show={show} onHide={onHide} centered scrollable>
      {/* Header */}
      <Modal.Header closeButton className="bg-light">
        <div className="d-flex align-items-center w-100">
          {stack.length > 0 && (
            <Button
              size="sm"
              variant="link"
              onClick={goBack}
              disabled={loading}
              className="p-0 me-2 text-primary"
              title="Go back"
            >
              <FaArrowLeft size={16} />
            </Button>
          )}
          <FaBox size={20} className="me-3 text-primary" />
          <ModalTitle>
            <h3 className="mb-0 text-primary">{data.Name}</h3>
            <small className="text-muted d-block" style={{ fontSize: "0.75rem" }}>
              {getTypeName(type)}
            </small>
          </ModalTitle>
          {( (type || defaultType) === "tasks" || (type || defaultType) === "orders") && (
            <div className="ms-auto d-flex align-items-center">
              <Button size="sm" variant="primary" onClick={handleEdit} disabled={loading}>
                Edit
              </Button>
            </div>
          )}
        </div>
      </Modal.Header>

      {/* Body */}
      <Modal.Body style={{ position: "relative" }}>
        {/* Data Sections */}
        {Object.entries(sections).map(([name, secs]) => (
          <div key={name} className="mb-4 bg-light p-3 rounded border">
            <h5 className="mb-3">{name}</h5>
            <Section name={name} data={secs} />
          </div>
        ))}

        {/* Related Tasks */}
        {!loc.pathname.includes("/task") && (
          <div className="mb-4 bg-light p-3 rounded border">
            <h5 className="mb-3">Related Tasks</h5>
            {tasks.length > 0 ? (
              <Table data={tasks} />
            ) : (
              <div className="text-center text-muted py-4">
                <FaTasks size={32} className="mb-2 opacity-25" />
                <p className="mb-0">No related tasks found</p>
              </div>
            )}
          </div>
        )}

        {/* Loading Overlay */}
        {loading && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(255, 255, 255, 0.8)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              borderRadius: "0.375rem",
              zIndex: 1000,
            }}
          >
            <div className="text-center">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3 text-muted">Loading record...</p>
            </div>
          </div>
        )}
      </Modal.Body>

      {/* Footer */}
      <Modal.Footer className="bg-light">
        <Button size="sm" variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default RecordModal;